/**
 * IGRA-21 — pure formatting + overview→viewmodel mapping helpers.
 *
 * Kept dependency-free (no i18n singleton, no store) so they are trivially
 * unit-testable per the task's acceptance criteria ("Unit: маппинг
 * overview→viewmodel, формат дат/цен (руб., без копеек)").
 */
import type { PriceExplainInput } from './circlePriceExplain'
import type { VpnOverviewResponse, VpnPeriod } from '../types/vpn'

/**
 * Решение оператора — адрес страницы подключения ОДИН на весь кабинет, и он без
 * uid: ключ по нему находит сам сервер, по сессии (`GET /app/vpn/connect`).
 *
 * uid в адресе — предъявительский секрет: по нему подключаются к VPN, и место
 * ему ровно одно — копируемая ссылка подписки (`key.url`), которую человек сам
 * несёт в приложение или на соседнее устройство. В кнопке, в истории браузера и
 * в реферере ему делать нечего.
 */
export const CONNECT_PAGE_URL = '/app/vpn/connect'

/**
 * Format a RUB amount as "1 990 ₽" — integer, space-grouped thousands, no
 * kopecks (ADR-001 §6: prices are whole rubles by design, "целые числа, без
 * копеек"). Deliberately NOT using `Intl.NumberFormat`/`toLocaleString`: the
 * grouping separator for `ru-RU` varies by ICU data version (regular space
 * vs. narrow no-break space U+202F) across Node/browser builds, which would
 * make this flaky in CI. A manual regex keeps the output byte-identical
 * everywhere.
 */
export function formatRub(amount: number): string {
  const rounded = Math.round(amount)
  const grouped = Math.abs(rounded).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  return `${rounded < 0 ? '-' : ''}${grouped} ₽`
}

/**
 * IGRA-404 — звёзды на витрине: «250,5» в ru, «250.5» в en. Дробные бывают
 * (спонсорская доля пяти минут — половина звезды), и терять их нельзя: человек
 * сравнивает своё число с соседским, а не «примерно».
 *
 * Правило то же, которым блок звёзд «Главной» показывает недельный прирост.
 */
export function formatStars(value: number, locale: string): string {
  return value.toLocaleString(locale)
}

/**
 * Приводит ОБЕ формы дат бэкенда к одному правилу — местному времени.
 *
 * Бэкенд отдаёт даты двумя видами: `Y-m-d H:i:s` (столбцы `vpn_*`, отданные
 * как есть) и голым `Y-m-d` (`personal_offer.until`, `pool.personal_until` —
 * их режет `substr`). JS парсит эти виды ПО-РАЗНОМУ: строку со временем как
 * местное, голую дату как UTC. Из-за этого в поясах западнее UTC «оплачено до
 * 30 сентября» показывалось как 29-е, а та же дата со временем — правильно.
 * Достраиваем голой дате полночь, и правило становится одно.
 *
 * Экспортируется для тестов формата.
 */
export function normalizeBackendDate(value: string): string {
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)) return value.replace(' ', 'T')
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return `${value}T00:00:00`

  return value
}

/**
 * Format an ISO datetime as a long localized date, e.g. "10 сентября 2026 г."
 * (ru) / "September 10, 2026" (en). Returns '' for a missing/invalid input so
 * callers can decide the empty-state copy.
 */
export function formatDateLong(iso: string | null | undefined, locale: string): string {
  if (!iso) return ''
  const date = new Date(normalizeBackendDate(iso))
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' }).format(date)
}

/** FLOW-V5 §8.2 п.4 — дата коротко («14 нояб.»): на кнопке мало места. */
export function formatDateShort(iso: string | null | undefined, locale: string): string {
  if (!iso) return ''
  const date = new Date(normalizeBackendDate(iso))
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(date)
}

/** FLOW-V5 §8.2 п.5 — дата со временем, когда до неё меньше суток. */
export function formatDateTime(iso: string | null | undefined, locale: string): string {
  if (!iso) return ''
  const date = new Date(normalizeBackendDate(iso))
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

/**
 * Состояние ДОСТУПА, и только его.
 *
 * HK-IGRA.664 — раньше `no_key` означал ещё и «доступ есть, а ключа нет», и
 * это стоило дорого: оплативший, которому бэкенд не выпустил ключ, читал на
 * «Главной» «Взять 3 дня за 3 ₽», а на `/connect` — «сначала оформи подписку».
 * Человеку сообщали неправду о его же деньгах. Ключ — отдельная правда
 * (`hasKey`/`keyUrl`), и экран решает по ней отдельно: ключа нет, а доступ
 * живой — значит его надо ВЫПУСТИТЬ, а не продавать подписку заново.
 */
export type SubscriptionStatusKind = 'no_key' | 'active' | 'expired'

export interface SubscriptionPeriodViewModel {
  period: VpnPeriod
  /** Сумма, которая уйдёт в счёт (`price`) — то, что читает человек на кнопке. */
  priceLabel: string
  /** Зачёркнутый прайс при скидке круга; `null` — зачёркивать нечего. */
  basePriceLabel: string | null
  hasDiscount: boolean
}

export interface SubscriptionCircleViewModel {
  members: number
  seats: number
  /** IGRA-47 — ЛИЧНАЯ сумма следующего списания (`circle.next_month_price`),
   * а не ступень коллективной сетки: бэкенд отдаёт её уже с доплатой за
   * докупленные устройства, и она совпадает с `pricing.next_charge_amount`.
   * Ничего не пересчитываем здесь — иначе «твоя цена» и «спишем» снова
   * разъедутся. */
  personalPriceLabel: string
}

export interface SubscriptionViewModel {
  statusKind: SubscriptionStatusKind
  hasKey: boolean
  keyUrl: string | null
  paidUntilLabel: string | null
  devicesUsed: number
  deviceLimit: number
  tgLinked: boolean
  tgBonusAvailable: boolean
  /** HK-IGRA.348 — «доступ у человека когда-либо заводили». Живой бэкенд
   * отдаёт `access: null`, пока не было ни пробника, ни оплаты: у такого
   * человека нет ни срока, ни бонусных дней, которые можно продлить.
   * HK-IGRA.664 — теперь это ровно `statusKind !== 'no_key'`: смысл «доступ
   * есть, а ключа нет» из `no_key` ушёл и живёт в `hasKey`. Поле оставлено
   * отдельным — его читают экраны, которым до ключа дела нет. */
  hasSubscription: boolean
  periods: SubscriptionPeriodViewModel[]
  circle: SubscriptionCircleViewModel | null
  /** IGRA-41 — input for the shared price explainer; `null` when the month is
   * charged at the list price (no discount → no explanation, task rule 2). */
  priceExplain: PriceExplainInput | null
}

/**
 * Map the raw `GET /vpn/overview` payload into the view-friendly shape the
 * Subscription screen renders. Pure function — no I/O, no singletons.
 */
export function mapOverviewToViewModel(overview: VpnOverviewResponse, locale: string): SubscriptionViewModel {
  // IGRA-30: живой бэкенд отдаёт access: null, пока доступ не активирован
  const access = overview.access
  const hasKey = overview.key !== null
  const statusKind: SubscriptionStatusKind = access === null
    ? 'no_key'
    : access.status === 'active'
      ? 'active'
      : 'expired'

  return {
    statusKind,
    hasKey,
    keyUrl: overview.key?.url ?? null,
    paidUntilLabel: access ? formatDateLong(access.paid_until, locale) || null : null,
    devicesUsed: access?.devices_used ?? 0,
    deviceLimit: access?.device_limit ?? 0,
    tgLinked: access?.tg_linked ?? false,
    tgBonusAvailable: access?.tg_bonus_available ?? true,
    hasSubscription: !!access,
    // Форма опции пришла к витринной: `price` — сколько платить, `base_price` —
    // прайс. Крупным всегда идёт `price`: у года с докупленными устройствами
    // личной цены нет, а счёт всё равно больше прайса (3 790 против 1 990), и
    // подпись от `base_price` называла бы человеку не ту сумму. Прайс нужен
    // только зачёркнутым — при скидке круга.
    periods: overview.pricing.periods.map((p) => {
      const hasDiscount = p.personal_price !== null && p.personal_price !== p.base_price
      return {
        period: p.period,
        priceLabel: formatRub(p.price),
        basePriceLabel: hasDiscount ? formatRub(p.base_price) : null,
        hasDiscount,
      }
    }),
    circle: overview.circle
      ? {
          members: overview.circle.members,
          seats: overview.circle.seats,
          personalPriceLabel: formatRub(overview.circle.next_month_price),
        }
      : null,
    priceExplain: buildPriceExplainInput(overview),
  }
}

/**
 * IGRA-41 — the renewal screen explains the SAME month price the next charge
 * will use: everything is taken from `GET /vpn/overview`, nothing is derived.
 * `null` — the month costs the list price, so there is nothing to explain.
 */
export function buildPriceExplainInput(overview: VpnOverviewResponse): PriceExplainInput | null {
  const month = overview.pricing.periods.find((period) => period.period === 'month')
  if (!month || month.personal_price === null || month.personal_price >= month.base_price) return null

  return {
    basePrice: month.base_price,
    personalPrice: month.personal_price,
    // HK-IGRA.55 — приглашённый ДО первой оплаты не член круга (`circle:
    // null`), и отличить его от обычного участника кабинету было нечем.
    // Признак приходит явным полем.
    invited: overview.pricing.invited === true,
    membersCount: overview.circle?.members ?? null,
    seats: overview.circle?.seats ?? null,
    nextPriceIfJoined: overview.circle?.next_price_if_joined ?? null,
    // Сетку overview не отдаёт — лестница живёт на экране круга (GET /vpn/circle).
    grid: null,
  }
}
